/*
 * Ryan O'Dowd
 * 2026-07-11
 * © Copyright 2026 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import LicensingInfo from '@components/LicensingInfo';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import {
  View,
} from 'react-native';
import WebPageStub from '@website/containers/WebPageStub';
import WebsiteSection from '@website/components/WebsiteSection';
import {
  fetchTranslations,
} from '@utils/store/actions';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const Bibles = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  const locales = hooks.useTypedSelector((state) => state.locales);

  useEffect(() => {
    dispatch(fetchTranslations());
  }, [dispatch]);

  const [_selectedTranslation, setSelectedTranslation] = useState(null);
  const selectedLocale = locales[_selectedTranslation?.locale_id] || {};

  const translationsReducer = hooks.useTypedSelector((state) => state.translations);
  const translations = useMemo(() => {
    return translationsReducer.filter((t) => !t.disabled);
  }, [translationsReducer]);

  const translationsTableRows = translations.map((t) => { // @TODO: convert these to accordion? when expanded, show all info for just this student (and class average)
    const locale = locales[t.locale_id];

    return [ // @TODO: update ids
      {
        id: `${t.id}_name`,
        content: (
          <span>
            <Text style={styles.namePrimaryText}>{t.name}</Text>{/* @TODO: update styles */}
            {t.name !== t.name_english && <Text style={styles.nameSecondaryText}>{t.name_english}</Text>}{/* @TODO: update styles */}
          </span>
        ),
        sortValue: t.name.toLocaleLowerCase(),
      },
      {
        id: `${t.id}_abbreviation`,
        content: <Text style={styles.abbreviationText}>{t.abbreviation}</Text>,
        sortValue: t.abbreviation.toLocaleLowerCase(),
      },
      {
        id: `${t.id}_locale`,
        content: (
          <span style={styles.localeWrapper}>
            <Text style={styles.localePrimaryText}>{`${locale.language_name_local}`}</Text>{/* @TODO: update styles */}
            {/* @TODO: put local code back in once they're not all en
            <Text>{`${locale.language_name_local} (${locale.datefns_code})`}</Text>{/ @TODO: update styles /}
            */}
            {locale.language_name_local !== locale.language_name && <Text style={styles.localeSecondaryText}>{locale.language_name}</Text>}{/* @TODO: update styles */}
          </span>
        ),
        sortValue: locale.language_name.toLocaleLowerCase(),
      },
      /* @TODO:
      {
        id: `${t.id}_version`,
        content: t.available_in_version_lexigraphic, // @TODO: make human readable // @TODO: tooltip for launch date
        sortValue: t.available_in_version_lexigraphic,
      },
      */
    ];
  });

  return (
    <WebPageStub>
      <WebsiteSection// @TODO: i18n this whole section
        isDarkBackground={true}
        title='Supported Bibles'
      >
        <View style={styles.verseWrapper}>{/* @TODO: convert this to Passage component */}{/* @TODO: abstract this with i18n page */}
          <Text style={styles.verseBody}>
            <Text style={styles.verseBody}>{'Then Jesus came to them and said, "All authority in heaven and on earth has been given to me. Therefore go and make disciples of '}</Text>
            <Text style={[styles.verseBody, styles.bold]}>all nations</Text>
            <Text style={styles.verseBody}>{', baptizing them in the name of the Father and of the Son and of the Holy Spirit, and teaching them to obey everything I have commanded you. And surely I am with you always, to the very end of the age."'}</Text>
          </Text>
          <Text style={styles.reference}>Matthew 28:18-20, NIV</Text>
        </View>
        <View style={styles.introTextWrapper}>
          <Text style={styles.introText}>{`Wrinkly currently supports ${translations.length} Bible texts available in ${Object.keys(locales).length} locales found in the table below.  To request another translation, please contact us using the form found on the `}<Text isLink={true} href='/'>home page</Text>{'.'}</Text>
        </View>
      </WebsiteSection>

      <WebsiteSection
        isDarkBackground={false}
      >
        <Table
          open={true}
          noAccordion={true}
          lightBackground={true}
          headerRowData={[
            {width: 500, id: 'name', content: 'Name'},
            {width: 75, id: 'abbreviation', content: 'Abbreviation', rightAlign: true},
            {width: 75, id: 'locale', content: 'Language', rightAlign: true},
            // @TODO: column for full/partial text
          ]}
          isSortable={true}
          orderByIndex={0}// @TODO: this should be an id, not the header index
          orderDirection='ascending'
          rowData={translationsTableRows}
          // @TODO: this should be an accordion to show all assignments for the selected student
          onRowPress={(row) => setSelectedTranslation(translations.find((t) => t.id === +row[0].id.split('_')[0]))}// @TODO: way too hacky
        />
      </WebsiteSection>

      {_selectedTranslation &&
        <Dialog
          visible={_selectedTranslation}
          onDismiss={() => setSelectedTranslation(null)}
        >
          <Dialog.Title>
            {_selectedTranslation.name}
          </Dialog.Title>
          <Dialog.Content>
            <Text style={styles.dialogText}><Text style={styles.dialogTextTitle}>Local name: </Text>{_selectedTranslation.name}</Text>
            {_selectedTranslation.name !== _selectedTranslation.name_english && <Text style={styles.dialogText}><Text style={styles.dialogTextTitle}>English name: </Text>{_selectedTranslation.name_english}</Text>}
            <Text style={styles.dialogText}><Text style={styles.dialogTextTitle}>Abbreviation: </Text>{_selectedTranslation.abbreviation}</Text>
            <Text style={styles.dialogText}><Text style={styles.dialogTextTitle}>Locale: </Text>{selectedLocale.language_name}{selectedLocale.language_name_local !== selectedLocale.language_name_local && ` (${selectedLocale.language_name})`}</Text>
            <Text style={styles.dialogText}><Text style={styles.dialogTextTitle}>First available in: </Text>v{utilities.lexigraphicToSemver(_selectedTranslation.available_in_version_lexigraphic)}</Text>{/* @TODO: include timestamp */}
            <View style={styles.dialogSpacer} />
            <LicensingInfo translationId={_selectedTranslation.id} />
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setSelectedTranslation(null)}
            >
              {i18n('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }

    </WebPageStub>
  );
};

export default Bibles;
